/**
 * 실행계획 탭을 다루는 규칙 — **한 곳에서.**
 *
 * 탭을 만드는 곳이 여섯 군데(수작업 계획, 튜닝 패널 단건·전체, 모니터링, 보관함, 에이전트)라
 * 상한과 변환이 각자 흩어지면 금세 갈린다. 실제로 상한이 **5(다섯 곳)와 10(한 곳)으로 갈려
 * 있었다.**
 */

/**
 * 실행계획 탭 상한.
 *
 * 에이전트 한 턴이 기준 1 + 대안 N 을 만들기 때문에 5 로는 사용자의 수작업 계획이 통째로
 * 밀려난다. 넉넉히 두되 무한은 아니다(계획 원문은 한 장에 수십 KB 다).
 */
export const MAX_PLAN_TABS = 12;

/**
 * 탭을 앞에 밀어 넣는다.
 *
 * - 같은 id 는 **자리를 지키며 갱신**한다(에이전트 재전송·같은 계획 재측정).
 * - 상한을 넘으면 뒤에서부터 버리되 **결론(비교) 탭과 보관함에서 연 탭은 건너뛴다** —
 *   사용자가 일부러 불러온 것과 결론이 먼저 사라지면 안 된다.
 * - 그래도 넘치면 가장 오래된 것부터.
 */
export function pushPlanTabs(prev, incoming, max = MAX_PLAN_TABS) {
    const add = Array.isArray(incoming) ? incoming : [incoming];
    if (!add.length) return prev;

    const byId = new Map(add.filter(Boolean).map(t => [t.id, t]));
    // 이미 있는 것은 제자리에서 갱신한다
    const kept = (prev || []).map(t => (byId.has(t.id) ? { ...t, ...byId.get(t.id) } : t));
    const updatedIds = new Set(kept.filter(t => byId.has(t.id)).map(t => t.id));
    const fresh = add.filter(t => t && !updatedIds.has(t.id));

    let out = [...fresh, ...kept];
    if (out.length <= max) return out;

    /** 쉽게 버려도 되는 것인가 — 결론·보관물은 지킨다 */
    const disposable = (t) => !t.compare && t.origin !== 'archive';
    while (out.length > max) {
        let idx = -1;
        for (let i = out.length - 1; i >= 0; i--) {
            if (disposable(out[i])) { idx = i; break; }
        }
        // 전부 지켜야 할 것뿐이면 가장 오래된 것을 버린다(무한히 쌓이게 두지 않는다)
        out.splice(idx >= 0 ? idx : out.length - 1, 1);
    }
    return out;
}

/**
 * 탭 하나 → 보관·내보내기용 항목 배열.
 * 비교 탭이면 전/후 두 항목, 아니면 한 항목.
 */
export function planTabToItems(tab, conn) {
    if (!tab) return [];
    const common = {
        connectionId: tab.connectionId || null,
        connName: (conn && conn.name) || null,
        dbType: (conn && conn.dbType) || null,
    };
    if (tab.compare) {
        const { before, after } = tab.compare;
        return [
            { ...common, role: 'before', label: (before && before.label) || null,
              sql: (before && before.sql) || null, planText: (before && before.planText) || '',
              mode: (before && before.mode) || null, metrics: (before && before.metrics) || null,
              captureId: (before && before.captureId) || null },
            { ...common, role: 'after', label: (after && after.label) || null,
              sql: (after && after.sql) || null, planText: (after && after.planText) || '',
              mode: (after && after.mode) || null, metrics: (after && after.metrics) || null,
              captureId: (after && after.captureId) || null },
        ];
    }
    return [{
        ...common,
        role: 'single',
        label: tab.name || null,
        sql: tab.query || null,
        planText: typeof tab.data === 'string' ? tab.data : '',
        mode: tab.mode || null,
        metrics: tab.metrics || null,
        captureId: tab.captureId || null,
        // SQL Monitor 리포트 원문 — 계획만이 아니라 실행한 함수의 결과를 함께 남긴다(2026-10-07)
        monitorReport: tab.monitorReport || null,
    }];
}

export default { MAX_PLAN_TABS, pushPlanTabs, planTabToItems };
